import _usePractiseStartMutation from "../__generated__/usePractiseStartMutation.graphql";
import { useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { MeQuery } from '../components/guards';
import { t } from '../i18n/t';
import { whyNotStarted } from '../session/refusals.ts';
import { startInput, type PractiseTarget } from './practiseTarget.ts';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';
import type { usePractiseStartMutation } from '../__generated__/usePractiseStartMutation.graphql';
const StartPractice = (_usePractiseStartMutation.hash && _usePractiseStartMutation.hash !== "ed9d592368898a89e156eef996d5020e" && console.error("The definition of 'usePractiseStartMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _usePractiseStartMutation);

/**
 * Start a conversation steered at one pattern or one word, and go to it.
 *
 * This is the practice hub's whole action. The hub does not teach: an outcome
 * is a record of what happened in a conversation — `markPracticeOutcome` takes
 * a `sessionId`, and `set_outcome` only revises a row `startSession` already
 * wrote — so a pattern is practised by having a conversation about it, and the
 * extraction pipeline scores it the way it scores everything else. A card that
 * marked its own outcome would be a second, weaker source of truth feeding §6's
 * schedule.
 *
 * A hook rather than a component, because every entry point needs it: the
 * hub's cards, the detail page at `/practice/:itemId`, a word's detail
 * (word-practice §7), whose outcome is likewise the conversation's to record,
 * and a finished book's Talk about it (storybooks phase 2 §3).
 *
 * The single-use audio token the payload carries is dropped on the navigation.
 * That is deliberate rather than wasteful: `SessionProvider` only wraps
 * `/session`, and `SessionView` mints a fresh token through `refreshAudioToken`
 * for any session it arrives at without credentials. Arriving from here is the
 * resume path with a different starting point.
 */
export function usePractise(): {
  practise: (target: PractiseTarget) => void;
  busy: boolean;
  why: string | null;
} {
  const navigate = useNavigate();
  // The query the guards above already ran, so Relay serves it from the store.
  const me = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  const learnerId = me.me?.learner?.id ?? null;
  const [startSession, busy] = useMutation<usePractiseStartMutation>(StartPractice);
  // A refusal said where it happened — a retired item, an unverified email, a
  // full server — rather than the button simply going quiet again.
  const [why, setWhy] = useState<string | null>(null);
  const practise = useCallback((target: PractiseTarget) => {
    // `busy` rather than an optimistic disable alone: `startSession` writes a
    // row, so a second tap while the first is in flight leaves an orphaned
    // active session behind and navigates to whichever answered last.
    if (!learnerId || busy) return;
    setWhy(null);
    startSession({
      variables: {
        input: startInput(learnerId, target)
      },
      onCompleted: res => {
        const id = res.startSession?.session?.id;
        // `replace`, so Back from the conversation does not return to the hub
        // card that started it and invite a second session.
        if (id) void navigate(`/session/${id}`, {
          replace: true
        });
      },
      onError: e => setWhy(t(whyNotStarted(e)))
    });
  }, [learnerId, busy, startSession, navigate]);
  return {
    practise,
    busy,
    why
  };
}